import React, { useState, useEffect } from "react";
import { getDatabase, ref, onValue, update, set } from "firebase/database";
import "./userSteelOrders.css";
import InfoIcon from "./infoIcon";

const SteelOrders = ({ adminView = false, selectedBrand = "" }) => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [adminReply, setAdminReply] = useState("");
  const [replyStatus, setReplyStatus] = useState("Processing");
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const gifUrl = "/Images/New notification.gif";

  // Fetch orders from Firebase
  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        const db = getDatabase();
        const ordersRef = ref(db, "steelOrder");

        onValue(ordersRef, (snapshot) => {
          if (snapshot.exists()) {
            const ordersData = snapshot.val();
            const ordersArray = [];

            // Convert the nested structure to a flat array
            Object.keys(ordersData).forEach((userId) => {
              Object.keys(ordersData[userId]).forEach((orderId) => {
                ordersArray.push({
                  id: orderId,
                  userId,
                  ...ordersData[userId][orderId],
                });
              });
            });

            // Sort by timestamp (newest first)
            ordersArray.sort((a, b) => {
              return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
            });

            setOrders(ordersArray);
          } else {
            setOrders([]);
          }
          setLoading(false);
        });
      } catch (error) {
        console.error("Error fetching orders:", error);
        setLoading(false);
      }
    };

    fetchOrders();
  }, [refreshTrigger]);

  // Filter orders based on search term, status, and selected brand
  useEffect(() => {
    let filtered = [...orders];

    // Filter by brand if in admin view and a brand is selected
    if (adminView && selectedBrand) {
      filtered = filtered.filter((order) => order.brand === selectedBrand);
    }

    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter((order) => order.status === statusFilter);
    }

    // Filter by search term (company name, order ID, or steel grade)
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (order) =>
          order.companyName?.toLowerCase().includes(term) ||
          order.id.toLowerCase().includes(term) ||
          order.steelGrade?.toLowerCase().includes(term),
      );
    }

    setFilteredOrders(filtered);
  }, [orders, searchTerm, statusFilter, adminView, selectedBrand]);

  const handleRefresh = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleReplySubmit = async () => {
    if (!selectedOrder || !adminReply.trim()) return;

    try {
      const db = getDatabase();
      const orderRef = ref(db, `steelOrder/${selectedOrder.userId}/${selectedOrder.id}`);

      // Get current Indian timestamp
      const now = new Date();
      const istOffset = 5.5 * 60 * 60 * 1000; // IST is UTC+5:30
      const istTime = new Date(now.getTime() + istOffset);
      const timestamp = istTime.toISOString().replace("T", " ").replace("Z", "");

      // Update the order with admin reply and new status
      await update(orderRef, {
        adminReply,
        adminReplyTimestamp: timestamp,
        status: replyStatus,
      });

      // Send notification to user
      const notificationRef = ref(db, `user_notifications/Steel Orders/${selectedOrder.userId}/${Date.now()}`);
      await set(notificationRef, {
        title: "Steel Order Update",
        gifUrl,
        message: `Your steel order (${selectedOrder.steelGrade} ${selectedOrder.steelDiameter}) has been ${replyStatus.toLowerCase()}.`,
        timestamp,
        seen: false,
        type: "order",
        orderId: selectedOrder.id,
      });

      // Close dialog and refresh orders
      setReplyDialogOpen(false);
      setAdminReply("");
      setReplyStatus("Processing");
      handleRefresh();
    } catch (error) {
      console.error("Error updating order:", error);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "Pending":
        return <span className="so-status-badge so-pending">Pending</span>;
      case "Processing":
        return <span className="so-status-badge so-processing">Processing</span>;
      case "Completed":
        return <span className="so-status-badge so-completed">Completed</span>;
      case "Cancelled":
        return <span className="so-status-badge so-cancelled">Cancelled</span>;
      default:
        return <span className="so-status-badge">{status}</span>;
    }
  };

  return (
    <div className="so-steel-orders-container">
      <div className="so-steel-orders-header">
        <h2>Steel Orders <InfoIcon text="The Steel Orders page provides a comprehensive overview of all steel procurement activities for the company. Each order is displayed with essential details such as the Company name, the Steel Details specifying the type and grade of steel, the Quantity ordered, and the expected Delivery Date. The Status column indicates the current stage of each order, whether it is pending, in transit, or completed, enabling efficient tracking and management. Additionally, the Actions section allows authorized personnel to perform relevant tasks such as updating order details, confirming deliveries, or generating reports. This centralized view ensures that steel inventory management is streamlined, reduces delays, and supports timely project execution." /></h2>
        <p>Manage steel orders from customers</p>
        <button className="so-refresh-button" onClick={handleRefresh}>
          <i className="so-refresh-icon">↻</i>
        </button>
      </div>

      <div className="so-steel-orders-filters">
        <div className="so-status-tabs">
          <button
            className={`so-tab-button ${statusFilter === "all" ? "so-active" : ""}`}
            onClick={() => setStatusFilter("all")}
          >
            All
          </button>
          <button
            className={`so-tab-button ${statusFilter === "Pending" ? "so-active" : ""}`}
            onClick={() => setStatusFilter("Pending")}
          >
            Pending
          </button>
          <button
            className={`so-tab-button ${statusFilter === "Processing" ? "so-active" : ""}`}
            onClick={() => setStatusFilter("Processing")}
          >
            Processing
          </button>
          <button
            className={`so-tab-button ${statusFilter === "Completed" ? "so-active" : ""}`}
            onClick={() => setStatusFilter("Completed")}
          >
            Completed
          </button>
        </div>

        <div className="so-search-container">
          <input
            type="search"
            placeholder="Search by company or grade..."
            className="so-search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <i className="so-search-icon">🔍</i>
        </div>
      </div>

      {loading ? (
        <div className="so-loading-container">
          <p>Loading orders...</p>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="so-empty-container">
          <p>No steel orders found.</p>
        </div>
      ) : (
        <div className="so-orders-table-container">
          <table className="so-orders-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Steel Details</th>
                <th>Quantity</th>
                <th>Delivery Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <tr key={order.id}>
                  <td className="so-company-cell">{order.companyName}</td>
                  <td>
                    <div className="so-steel-details">
                      <span className="so-brand">{order.brand}</span>
                      <span className="so-grade">
                        {order.steelGrade} {order.steelDiameter}
                      </span>
                    </div>
                  </td>
                  <td>{order.quantity} tonnes</td>
                  <td>{order.deliveryDate}</td>
                  <td>{getStatusBadge(order.status)}</td>
                  <td>
                    <button
                      className="so-reply-button"
                      onClick={() => {
                        setSelectedOrder(order);
                        setReplyDialogOpen(true);
                        setAdminReply(order.adminReply || "");
                        setReplyStatus(order.status);
                      }}
                    >
                      {order.adminReply ? "View/Edit Reply" : "Reply"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {replyDialogOpen && (
        <div className="so-modal-overlay">
          <div className="so-reply-modal">
            <div className="so-modal-header">
              <h3>{selectedOrder?.adminReply ? "Update Reply" : "Reply to Order"}</h3>
              <button className="so-close-button" onClick={() => setReplyDialogOpen(false)}>
                ×
              </button>
            </div>

            {selectedOrder && (
              <div className="so-modal-content so-scrollable-content">
                <div className="so-order-details-grid">
                  <div className="so-detail-group">
                    <h4>Company</h4>
                    <p>{selectedOrder.companyName}</p>
                  </div>
                  <div className="so-detail-group">
                    <h4>Steel Details</h4>
                    <p>
                      {selectedOrder.brand} - {selectedOrder.steelGrade} {selectedOrder.steelDiameter}
                    </p>
                  </div>
                </div>

                <div className="so-order-details-grid">
                  <div className="so-detail-group">
                    <h4>Quantity</h4>
                    <p>{selectedOrder.quantity} tonnes</p>
                  </div>
                  <div className="so-detail-group">
                    <h4>Delivery Date</h4>
                    <p>{selectedOrder.deliveryDate}</p>
                  </div>
                </div>

                <div className="so-detail-group so-full-width">
                  <h4>Delivery Address</h4>
                  <p>{selectedOrder.deliveryAddress}</p>
                </div>

                {selectedOrder.remarks && (
                  <div className="so-detail-group so-full-width">
                    <h4>Customer Remarks</h4>
                    <p>{selectedOrder.remarks}</p>
                  </div>
                )}

                <div className="so-form-group">
                  <label htmlFor="status">Order Status</label>
                  <select
                    id="status"
                    value={replyStatus}
                    onChange={(e) => setReplyStatus(e.target.value)}
                    className="so-status-select"
                  >
                    <option value="Pending">Pending</option>
                    <option value="Processing">Processing</option>
                    <option value="Completed">Completed</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </div>

                <div className="so-form-group">
                  <label htmlFor="reply">Reply Message</label>
                  <textarea
                    id="reply"
                    placeholder="Enter your reply to the customer..."
                    rows={5}
                    value={adminReply}
                    onChange={(e) => setAdminReply(e.target.value)}
                    className="so-reply-textarea"
                  />
                </div>
              </div>
            )}

            <div className="so-modal-footer">
              <button className="so-submit-button" onClick={handleReplySubmit}>
                <span className="so-send-icon">📤</span>
                {selectedOrder?.adminReply ? "Update Reply" : "Send Reply"}
              </button>
              <button className="so-cancel-button" onClick={() => setReplyDialogOpen(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SteelOrders;